import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import {
  ChevronDown,
  ChevronUp,
  GraduationCap,
  Layers,
  Search,
  Sun,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '@/lib/apiBase';
import { cn } from '@/lib/utils';
import { Input } from '../ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '../ui/accordion';
import { TutorialDetailView } from './TutorialDetailView';
import {
  HelpMisAJourBadge,
  HelpNouveauBadge,
} from './TutorialStatusBadge';
import {
  effectiveHelpStatusFromTutorial,
  getTutorialHelpBadge,
} from './helpStatus';

/** Nom affiché sous « Centre d’aide » (build : REACT_APP_APPLICATION_NAME). */
const APPLICATION_DISPLAY_NAME =
  (process.env.REACT_APP_APPLICATION_NAME || '').trim() || "Prysm'Analytics";

/** Ligne « Parcours terminé » sous la description (sans doublon avec les badges titre). */
function TutorialCardStatusText({ tutorial }) {
  const status = effectiveHelpStatusFromTutorial(tutorial);
  if (status !== 'a_jour') return null;
  return (
    <span
      className="text-[11px] font-normal leading-snug text-emerald-600 dark:text-emerald-400"
      role="status"
    >
      Parcours terminé
    </span>
  );
}

function tutorialListUsesVideoIcon(tutorial) {
  const t = String(tutorial?.type || tutorial?.content_type || tutorial?.format || '')
    .trim()
    .toLowerCase();
  if (t === 'video' || t === 'vidéo') return true;
  if (tutorial?.video_url || tutorial?.videoUrl) return true;
  return false;
}

/** Sous-chaîne insensible à la casse sur le titre et la description. */
function tutorialMatchesQuery(tutorial, queryLower) {
  if (!queryLower) return true;
  const title = String(tutorial?.titre || tutorial?.title || '').toLowerCase();
  const desc = String(tutorial?.description || '').toLowerCase();
  return title.includes(queryLower) || desc.includes(queryLower);
}

function TutorialCard({ tutorial, onOpen }) {
  const titre = tutorial.titre || 'Sans titre';
  const description = tutorial.description || '';
  const badge = getTutorialHelpBadge(tutorial);
  const Icon = tutorialListUsesVideoIcon(tutorial) ? Sun : Layers;

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => tutorial.id && onOpen?.(tutorial.id)}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && tutorial.id) {
          e.preventDefault();
          onOpen?.(tutorial.id);
        }
      }}
      className={cn(
        'cursor-pointer rounded-lg border border-border bg-background p-3 transition-colors',
        'hover:border-border hover:bg-accent',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
      data-testid={tutorial.id ? `help-tutorial-${tutorial.id}` : undefined}
    >
      <div className="flex gap-3">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground"
          aria-hidden
        >
          <Icon className="h-4 w-4" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-medium leading-snug text-foreground">{titre}</p>
              {description ? (
                <p className="text-xs font-normal leading-snug text-muted-foreground">
                  {description}
                </p>
              ) : null}
              <TutorialCardStatusText tutorial={tutorial} />
            </div>
            <div className="flex shrink-0 flex-wrap content-start justify-end gap-1.5">
              {badge === 'new' ? <HelpNouveauBadge /> : null}
              {badge === 'updated' ? <HelpMisAJourBadge /> : null}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export function HelpCenterPanel({ onClose, onHelpPendingRefresh }) {
  const { token } = useAuth();
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openTutorialId, setOpenTutorialId] = useState(null);
  const [openTutorialThemeNom, setOpenTutorialThemeNom] = useState('');
  const [themesRefreshKey, setThemesRefreshKey] = useState(0);
  /** Valeur du thème ouvert ; '' = tout replié (accordéon single + collapsible). */
  const [openTheme, setOpenTheme] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const searchQueryLower = useMemo(
    () => searchQuery.trim().toLowerCase(),
    [searchQuery],
  );

  const displayThemes = useMemo(() => {
    if (!searchQueryLower) return themes;
    return themes
      .map((theme) => {
        const raw = Array.isArray(theme.tutorials) ? theme.tutorials : [];
        const tutorials = raw.filter((t) => tutorialMatchesQuery(t, searchQueryLower));
        return { ...theme, tutorials };
      })
      .filter((theme) => (Array.isArray(theme.tutorials) ? theme.tutorials.length : 0) > 0);
  }, [themes, searchQueryLower]);

  const themeValueKeys = useMemo(
    () => displayThemes.map((theme, index) => String(theme.id ?? `theme-${index}`)),
    [displayThemes],
  );

  useEffect(() => {
    if (openTheme && !themeValueKeys.includes(openTheme)) {
      setOpenTheme('');
    }
  }, [openTheme, themeValueKeys]);

  const handlePanelClose = useCallback(() => {
    setSearchQuery('');
    onClose?.();
  }, [onClose]);

  const bumpThemesRefresh = useCallback(() => {
    setThemesRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/help/video-themes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          const data = Array.isArray(res.data) ? res.data : [];
          setThemes(data);
          onHelpPendingRefresh?.(data);
        }
      } catch (e) {
        if (!cancelled) {
          setError('Impossible de charger les thèmes.');
          setThemes([]);
          onHelpPendingRefresh?.([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token, themesRefreshKey, onHelpPendingRefresh]);

  const handleOpenTutorial = (themeNom, tutorialId) => {
    setOpenTutorialThemeNom(themeNom || '');
    setOpenTutorialId(tutorialId);
  };

  const handleBackFromTutorial = () => {
    setOpenTutorialId(null);
    setOpenTutorialThemeNom('');
  };

  if (openTutorialId && token) {
    return (
      <TutorialDetailView
        tutorialId={openTutorialId}
        themeNom={openTutorialThemeNom}
        token={token}
        onBack={handleBackFromTutorial}
        onCloseHelp={handlePanelClose}
        onTutorialProgressChanged={bumpThemesRefresh}
      />
    );
  }

  return (
    <aside
      data-testid="help-center-sheet"
      className="sticky top-0 flex h-screen max-h-screen min-h-0 min-w-[300px] w-[min(25vw,540px)] max-w-full shrink-0 animate-in slide-in-from-right fade-in duration-300 flex-col overflow-hidden rounded-t-2xl border-l border-border bg-background shadow-lg"
      aria-labelledby="help-center-title"
    >
      <header className="relative shrink-0 border-b border-border bg-background pt-6 pb-2 pl-6 pr-14">
        <button
          type="button"
          onClick={handlePanelClose}
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-xl border border-border text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 dark:hover:bg-[hsl(217,33%,17%)]"
          aria-label="Fermer le centre d'aide"
        >
          <X className="h-[18px] w-[18px]" strokeWidth={2} />
        </button>
        <div className="flex items-start gap-3 text-left">
          <GraduationCap
            className="h-5 w-5 shrink-0 text-muted-foreground"
            strokeWidth={1.5}
            aria-hidden
          />
          <div className="flex min-w-0 flex-col gap-1">
            <h2
              id="help-center-title"
              className="text-lg font-semibold leading-snug text-foreground"
            >
              Centre d&apos;aide
            </h2>
            <p className="text-sm font-normal leading-normal text-muted-foreground">
              {APPLICATION_DISPLAY_NAME}
            </p>
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain">
        <div className="sticky top-0 z-[1] border-b border-border bg-background px-6 py-3">
          <label htmlFor="help-center-search" className="sr-only">
            Rechercher un tutoriel par titre ou description
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={2}
              aria-hidden
            />
            <Input
              id="help-center-search"
              type="search"
              autoComplete="off"
              placeholder="Rechercher un tutoriel…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 border-border bg-background pl-9 text-sm text-foreground placeholder:text-muted-foreground"
            />
          </div>
        </div>

        <div className="px-6 py-4">
        {loading && (
          <p className="text-sm text-muted-foreground">Chargement des thèmes…</p>
        )}
        {!loading && error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
        {!loading && !error && themes.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Aucun thème disponible pour le moment.
          </p>
        )}
        {!loading && !error && searchQueryLower && displayThemes.length === 0 && themes.length > 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Aucun tutoriel ne correspond à votre recherche.
          </p>
        )}
        {!loading && !error && displayThemes.length > 0 && (
          <Accordion
            type="single"
            collapsible
            className="w-full border-0"
            value={openTheme}
            onValueChange={setOpenTheme}
          >
            {displayThemes.map((theme, index) => {
              const value = String(theme.id ?? `theme-${index}`);
              const label = theme.nom || 'Sans titre';
              const list = Array.isArray(theme.tutorials) ? theme.tutorials : [];
              const themeHasFreshTutorial = list.some((t) => {
                const b = getTutorialHelpBadge(t);
                return b === 'new' || b === 'updated';
              });
              const isExpanded = openTheme === value;
              const showThemeNouveauBadge =
                !isExpanded && themeHasFreshTutorial;
              return (
                <AccordionItem
                  key={value}
                  value={value}
                  className="w-full border-0 border-b border-border last:border-b-0"
                >
                  <AccordionPrimitive.Header className="flex w-full">
                    <AccordionPrimitive.Trigger
                      className={cn(
                        'group flex w-full flex-1 items-center justify-between gap-2 border-0 bg-transparent py-4 text-left outline-none transition-colors',
                        'hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                        {label}
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        {showThemeNouveauBadge ? <HelpNouveauBadge /> : null}
                        <span
                          className="relative h-4 w-4 shrink-0 text-muted-foreground"
                          aria-hidden
                        >
                          <ChevronDown
                            className="absolute left-0 top-0 h-4 w-4 transition-opacity group-data-[state=open]:pointer-events-none group-data-[state=open]:opacity-0"
                            strokeWidth={2}
                          />
                          <ChevronUp
                            className="absolute left-0 top-0 h-4 w-4 opacity-0 transition-opacity group-data-[state=open]:opacity-100"
                            strokeWidth={2}
                          />
                        </span>
                      </span>
                    </AccordionPrimitive.Trigger>
                  </AccordionPrimitive.Header>
                  <AccordionContent className="pb-4 pt-0">
                    {Array.isArray(theme.tutorials) && theme.tutorials.length > 0 ? (
                      <div className="flex flex-col space-y-2 pl-1">
                        {theme.tutorials.map((tutorial, ti) => (
                          <TutorialCard
                            key={String(tutorial.id ?? `t-${ti}`)}
                            tutorial={tutorial}
                            onOpen={(id) =>
                              handleOpenTutorial(label, id)
                            }
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Aucun tutoriel pour ce thème.
                      </p>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
        </div>
      </div>
    </aside>
  );
}
